"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import Modal from "@/app/(app)/library/components/ui/Modal";
import TourOverlay from "./TourOverlay";
import TourTooltip from "./TourTooltip";
import styles from "./OnboardingTour.module.css";
import tooltipStyles from "./TourTooltip.module.css";

const OPEN_UPLOAD_MODAL_EVENT = "flare:open-upload-modal";

const SPOTLIGHT_STEPS = [
  {
    id: "projects",
    target: '[data-tour="project-tabs"]',
    placement: "bottom",
    interactive: false,
    button: "Next",
  },
  {
    id: "sanders",
    target: '[data-tour="sanders-tab"]',
    placement: "bottom",
    interactive: true,
    button: null,
  },
  {
    id: "folders",
    target: '[data-tour="folder-row"]',
    placement: "bottom",
    interactive: true,
    button: null,
  },
  {
    id: "actions",
    target: "[data-tour-highlight]",
    placement: "left",
    interactive: false,
    button: "Next",
  },
  {
    id: "notifications",
    target: '[data-tour="notifications"]',
    placement: "bottom",
    interactive: false,
    button: "Next",
  },
  {
    id: "profile-click",
    target: '[data-tour="profile-menu"]',
    placement: "bottom",
    interactive: true,
    button: null,
  },
  {
    id: "profile-menu",
    target: '[role="menu"]',
    placement: "left",
    interactive: false,
    button: "Next",
  },
  {
    id: "chat-widget",
    target: '[data-tour="chat-widget"]',
    placement: "left",
    interactive: false,
    button: "Got it",
  },
];

// ── Step content with highlighted text ──────────────────────────
function StepContent({ stepId }) {
  const h = tooltipStyles.highlight;
  const clickHint = tooltipStyles.clickHint;

  switch (stepId) {
    case "projects":
      return (
        <>
          <p className={tooltipStyles.title}>Your projects</p>
          <p className={tooltipStyles.text}>
            Projects let you <span className={h}>group related videos</span> together.
            Each project acts as a workspace for a specific topic or client.
          </p>
        </>
      );
    case "sanders":
      return (
        <>
          <p className={tooltipStyles.title}>Try it out</p>
          <p className={tooltipStyles.text}>
            We've set up a sample project for you.
          </p>
          <p className={clickHint}>
            <ClickIcon />
            Click on "Sanders tutorial" to continue
          </p>
        </>
      );
    case "folders":
      return (
        <>
          <p className={tooltipStyles.title}>Folders</p>
          <p className={tooltipStyles.text}>
            Inside a project, <span className={h}>folders</span> help you organize videos
            into categories. You can drag videos into folders or create new ones.
          </p>
          <p className={clickHint}>
            <ClickIcon />
            Click on a folder to continue
          </p>
        </>
      );
    case "actions":
      return (
        <>
          <p className={tooltipStyles.title}>Quick actions</p>
          <p className={tooltipStyles.text}>
            Hover over any video to access quick actions:{" "}
            <span className={h}>view analytics</span>, or{" "}
            <span className={h}>customize the embed</span> player.
            <span className={h}>open action menu</span>,{" "}
          </p>
        </>
      );
    case "notifications":
      return (
        <>
          <p className={tooltipStyles.title}>Notifications</p>
          <p className={tooltipStyles.text}>
            This is your <span className={h}>notification center</span>. You'll be notified
            when someone <span className={h}>leaves a comment</span> on your videos, and
            for important updates about your account.
          </p>
        </>
      );
    case "profile-click":
      return (
        <>
          <p className={tooltipStyles.title}>Your profile</p>
          <p className={tooltipStyles.text}>
            Your profile menu gives you access to important settings and tools.
          </p>
          <p className={clickHint}>
            <ClickIcon />
            Click the profile icon to continue
          </p>
        </>
      );
    case "profile-menu":
      return (
        <>
          <p className={tooltipStyles.title}>Your menu</p>
          <p className={tooltipStyles.text}>
            From here you can manage your{" "}
            <span className={h}>profile &amp; billing</span>, or view{" "}
            <span className={h}>analytics</span> across all your videos.
          </p>
        </>
      );
    case "chat-widget":
      return (
        <>
          <p className={tooltipStyles.title}>Direct chat</p>
          <p className={tooltipStyles.text}>
            Have a question or need help? Send a message here for{" "}
            <span className={h}>direct contact with the Flare team</span>.
            We read every message and respond as quickly as we can.
          </p>
        </>
      );
    default:
      return null;
  }
}

function ClickIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3.5V2M5.06 5.06L4 4M5.06 13.94L4 15M13.94 5.06L15 4M3.5 9H2M8 14v7l3-3 2.5 1L14 14" />
      <circle cx="9" cy="9" r="5" />
    </svg>
  );
}

export default function OnboardingTour({ isOpen, markOnboardingComplete }) {
  // 0..N (spotlight index) | "final" | "done"
  // The fullscreen onboarding slideshow now owns the "Welcome to Flare"
  // intro, so the tour starts directly at its first feature spotlight.
  const [phase, setPhase] = useState(0);
  const injectedStylesRef = useRef([]);
  const blockMenuCloseRef = useRef(false);

  // ── Persistent mousedown blocker for profile dropdown ─────────
  // Registered on mount (before any context menu opens) so it fires
  // first in capture phase and can block the menu's close handler.
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!blockMenuCloseRef.current) return;
      const menu = document.querySelector('[role="menu"]');
      const tooltip = document.querySelector("[data-tour-tooltip]");
      if (tooltip?.contains(e.target)) return;
      if (menu?.contains(e.target)) return;
      e.stopImmediatePropagation();
    };
    const onKeyDown = (e) => {
      if (!blockMenuCloseRef.current) return;
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    const onResize = (e) => {
      if (!blockMenuCloseRef.current) return;
      e.stopImmediatePropagation();
    };
    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", onResize, true);
    return () => {
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", onResize, true);
    };
  }, []);

  // ── Inject dynamic styles per step ────────────────────────────
  useEffect(() => {
    injectedStylesRef.current.forEach((s) => s.remove());
    injectedStylesRef.current = [];

    const step = typeof phase === "number" ? SPOTLIGHT_STEPS[phase] : null;
    if (!step) return;

    const inject = (css) => {
      const el = document.createElement("style");
      el.textContent = css;
      document.head.appendChild(el);
      injectedStylesRef.current.push(el);
    };

    if (step.id === "actions") {
      inject("[data-tour-highlight] { opacity: 1 !important; }");
    }
    if (step.id === "profile-menu") {
      inject('[role="menu"] { z-index: 10002 !important; }');
    }

    return () => {
      injectedStylesRef.current.forEach((s) => s.remove());
      injectedStylesRef.current = [];
    };
  }, [phase]);

  // ── Mark the middle video card's actions for highlighting ─────
  useEffect(() => {
    const step = typeof phase === "number" ? SPOTLIGHT_STEPS[phase] : null;
    if (step?.id !== "actions") return;

    const mark = () => {
      document.querySelectorAll("[data-tour-highlight]").forEach((el) =>
        el.removeAttribute("data-tour-highlight"),
      );
      const all = document.querySelectorAll('[data-tour="video-actions"]');
      if (all.length === 0) return;
      const midIndex = Math.floor(all.length / 2);
      all[midIndex].setAttribute("data-tour-highlight", "");
    };

    mark();
    const t = setTimeout(mark, 500);
    const observer = new MutationObserver(mark);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      observer.disconnect();
      document.querySelectorAll("[data-tour-highlight]").forEach((el) =>
        el.removeAttribute("data-tour-highlight"),
      );
    };
  }, [phase]);

  // ── Interactive step click listeners ──────────────────────────
  useEffect(() => {
    if (typeof phase !== "number") return;
    const step = SPOTLIGHT_STEPS[phase];
    if (!step) return;

    const advance = (delay = 0) => {
      const go = () => setPhase((prev) => (typeof prev === "number" ? prev + 1 : prev));
      if (delay > 0) setTimeout(go, delay);
      else go();
    };

    if (step.id === "sanders") {
      const handler = (e) => {
        if (e.target.closest('[data-tour="sanders-tab"]')) advance(600);
      };
      document.addEventListener("click", handler, true);
      return () => document.removeEventListener("click", handler, true);
    }

    if (step.id === "folders") {
      const handler = (e) => {
        if (e.target.closest('[data-tour="folder-item"]')) advance(400);
      };
      document.addEventListener("click", handler, true);
      return () => document.removeEventListener("click", handler, true);
    }

    if (step.id === "profile-click") {
      const handler = (e) => {
        if (e.target.closest('[data-tour="profile-menu"]')) {
          blockMenuCloseRef.current = true;
          advance(300);
        }
      };
      document.addEventListener("click", handler, true);
      return () => document.removeEventListener("click", handler, true);
    }
  }, [phase]);

  // ── Close menu and advance to final ────────────────────────────
  const advanceToFinal = useCallback(() => {
    blockMenuCloseRef.current = false;
    // Close the context menu after unblocking
    setTimeout(() => {
      document.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    }, 50);
    setPhase("final");
  }, []);

  const handleComplete = useCallback(
    (useCasePreference = null) => {
      blockMenuCloseRef.current = false;
      setPhase("done");
      markOnboardingComplete(useCasePreference);
    },
    [markOnboardingComplete],
  );

  const advanceSpotlight = useCallback(() => {
    setPhase((prev) => {
      if (typeof prev !== "number") return prev;
      const next = prev + 1;
      if (next >= SPOTLIGHT_STEPS.length) return "final";
      return next;
    });
  }, []);

  const handleUploadClick = useCallback(() => {
    handleComplete(null);
    window.dispatchEvent(new CustomEvent(OPEN_UPLOAD_MODAL_EVENT));
  }, [handleComplete]);

  if (!isOpen || phase === "done") return null;

  // ── Modal: Final CTA ──────────────────────────────────────────
  if (phase === "final") {
    return (
      <Modal isOpen>
        <div className={styles.modalContent}>
          <Image src="/icons/FlareIcon.png" alt="Flare" width={48} height={48} className={styles.logo} priority />
          <h2 className={styles.modalTitle}>You're all set!</h2>
          <p className={styles.modalText}>
            Ready to create your first Flare? <strong>Upload</strong> a video you
            already have to get started.
          </p>
          <div className={styles.finalButtons}>
            <button type="button" className={styles.primaryBtn} onClick={handleUploadClick}>
              Upload a video
            </button>
          </div>
          <button type="button" className={styles.laterBtn} onClick={() => handleComplete(null)}>
            I'll do this later
          </button>
        </div>
      </Modal>
    );
  }

  // ── Spotlight phases ──────────────────────────────────────────
  if (typeof phase === "number") {
    const step = SPOTLIGHT_STEPS[phase];
    if (!step) return null;

    const dots = SPOTLIGHT_STEPS.map((s, i) => (
      <span
        key={s.id}
        className={`${tooltipStyles.dot} ${i <= phase ? tooltipStyles.dotActive : ""}`}
      />
    ));

    const isProfileMenu = step.id === "profile-menu";
    const isChatWidget = step.id === "chat-widget";

    const handleStepButton = isProfileMenu
      ? () => {
          // Close menu, then advance to next step (chat widget)
          blockMenuCloseRef.current = false;
          setTimeout(() => {
            document.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
          }, 50);
          advanceSpotlight();
        }
      : isChatWidget
        ? advanceToFinal
        : advanceSpotlight;

    return (
      <>
        <TourOverlay
          targetSelector={step.target}
          interactive={step.interactive}
          padding={step.id === "actions" ? 6 : 12}
          borderRadius={
            step.id === "profile-click" || step.id === "profile-menu"
              ? 20
              : step.id === "notifications" || step.id === "chat-widget"
                ? 16
                : 12
          }
        />
        <TourTooltip targetSelector={step.target} placement={step.placement}>
          <StepContent stepId={step.id} />
          <div className={tooltipStyles.actions}>
            <div className={tooltipStyles.stepIndicator}>{dots}</div>
            {step.button && (
              <button
                type="button"
                className={tooltipStyles.primaryBtn}
                onClick={handleStepButton}
              >
                {step.button}
              </button>
            )}
          </div>
        </TourTooltip>
      </>
    );
  }

  return null;
}
